import { type Platform } from "@forento/core/models/platform"

export type DashboardNoticeId =
	| "forento-ai-claimed"
	| "free-trial"
	| "creator-highlights"
	| "creator-community"
	| "forento-ai-offer"

/** How long to wait after a notice is dismissed or acted on before showing the next one. */
export const dashboardNoticeDelayDays = 3

/** Matches the gift length in `startAddonTrialByPlatform`, so the trial start can be derived from its end. */
const addonTrialGiftDays = 7

const dayMs = 24 * 60 * 60 * 1000

type NoticeContext = { platform: Platform; publishedOfferCount: number; now?: Date }

/**
 * Picks the single dashboard notice to show. Notices are listed in priority order; the first eligible one wins
 * unless another notice was resolved within the delay, in which case only follow-ups to that action may show.
 */
export function getDashboardNotice({ platform, publishedOfferCount, now = new Date() }: NoticeContext) {
	const hasForentoAi =
		platform.aiBetaCreditGrantDate !== null || platform.enabledFeatures.includes("ai-generated-courses")
	const { plan } = platform
	const isLifetimePlan = plan.type === "appsumo" || plan.type === "viededingue" || plan.type === "productcanyon"

	const notices: { id: DashboardNoticeId; isEligible: boolean; bypassesDelay?: boolean }[] = [
		{
			// Confirms the credit claim the creator just made, so it follows the offer without waiting
			id: "forento-ai-claimed",
			isEligible: platform.aiBetaCreditGrantDate !== null && platform.aiBetaBannerDismissDate === null,
			bypassesDelay: true,
		},
		{
			id: "free-trial",
			isEligible:
				(plan.type === "appsumo" || plan.type === "viededingue" || plan.type === "productcanyon") &&
				plan.addon === null &&
				platform.trialEndDate === null &&
				platform.trialDismissDate === null,
		},
		{
			id: "creator-highlights",
			isEligible: publishedOfferCount > 0 && platform.creatorHighlightsPopupDismissDate === null,
		},
		{
			id: "creator-community",
			isEligible: hasForentoAi && platform.creatorCommunityBannerDismissDate === null,
		},
		{
			// The offer can't be dismissed, so it comes last to avoid blocking every other notice until claimed
			id: "forento-ai-offer",
			isEligible: !hasForentoAi,
		},
	]

	const resolveDates = [
		platform.aiBetaBannerDismissDate,
		platform.trialDismissDate,
		isLifetimePlan && platform.trialEndDate !== null
			? new Date(platform.trialEndDate.getTime() - addonTrialGiftDays * dayMs)
			: null,
		platform.creatorHighlightsPopupDismissDate,
		platform.creatorCommunityBannerDismissDate,
	].filter(date => date !== null)
	const lastResolveTime = Math.max(...resolveDates.map(date => date.getTime()))
	const isDelayed = now.getTime() < lastResolveTime + dashboardNoticeDelayDays * dayMs

	return notices.find(notice => notice.isEligible && (notice.bypassesDelay || !isDelayed))?.id ?? null
}
